import {
  ActionIcon,
  Anchor,
  AppShell,
  Avatar,
  Box,
  Button,
  Divider,
  Group,
  Menu,
  Transition,
  UnstyledButton,
} from "@mantine/core";
import { useTimeout } from "@mantine/hooks";
import {
  IconChevronDown,
  IconChevronLeft,
  IconChevronsUp,
  IconCoin,
  IconCurrencyDollar,
  IconDeviceMobile,
  IconLayoutGrid,
  IconLogout,
  IconQuestionMark,
  IconSettings,
} from "@tabler/icons-react";
import { useTranslation } from "next-i18next";
import Link from "next/link";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { isAndroid } from "react-device-detect";
import { useBoard, useIsBoardAdmin } from "../../common/boards/board.hooks";
import { useAppStore } from "../../common/stores/app.store";
// import { useRequireAuth } from "../common/auth/useRequireAuth"
import { useAuthStore } from "../../common/stores/auth.store";
import { getBoardLink, getInitials, PLAY_STORE_LINK } from "../../common/util";
import BoardLink from "../boards/BoardLink";
import Hider from "../common/Hider";
import SchemeToggle from "../common/SchemeToggle";
import NotificationsButton from "../notifications/NotificationsButton";
import classes from "./Header.module.css";
import Logo from "./Logo";
import PromptTip from "../common/PromptTip";
// const FRESHDESK_URL = "https://scoreboarder.freshdesk.com/support/home";
const HeaderComp = () => {
  const { t } = useTranslation(["common"]);
  // const toggleOpen = useAppStore((state) => state.toggleOpen);
  const user = useAuthStore((state) => state.user);
  const token = useAuthStore((state) => state.token);
  // console.log("user :", user);
  const { board } = useBoard();
  const logout = useAuthStore((state) => state.logout);
  const [showBack, setShowBack] = useState(false);
  const [showHome, setShowHome] = useState(false);
  const router = useRouter();
  const isLogin =
    router.pathname.indexOf("/login") === 0 ||
    router.pathname.indexOf("/pwa-home") === 0;
  const isBoard = router.pathname.indexOf("/boards") === 0;
  const isBoardAdmin = useIsBoardAdmin();
  // console.log("isBoard :", isBoard);

  const [userChecked, setUserChecked] = useState(false);
  useTimeout(() => setUserChecked(true), 1000, {
    autoInvoke: true,
  });
  useEffect(() => {
    if (token) setUserChecked(true);
  }, [token]);
  const doLogout = () => {
    logout();
    router.push(isPwa ? "pwa-home" : "/");
  };
  const isPwa = useAppStore((s) => s.isPwa);
  useEffect(() => {
    // console.log("router.asPath :", router.asPath);
    //show home if we're on root of a board, show back if we're on a board or deeper e.g. http://localhost:3001/boards/i98s8Q3t/admin-payments
    const newShowHome =
      router.asPath.indexOf("/boards/") === 0 &&
      router.asPath.split("/").length === 4; // e.g. /boards/i98s8Q3t
    // console.log(
    //   'router.asPath.split("/").length  :',
    //   router.asPath.split("/").length,
    // );
    setShowHome(newShowHome);
    const newShowBack =
      !newShowHome &&
      router.asPath !== "/" &&
      router.asPath !== "/dashboard" &&
      router.asPath !== "/pwa-home";
    setShowBack(newShowBack);
    // console.log("newShowHome :", newShowHome);
    // console.log("newShowBack :", newShowBack);
  }, [router.asPath]);
  // const showBack =
  //   router.asPath !== "/" &&
  //   router.asPath !== "/dashboard" &&
  //   router.asPath !== "/pwa-home";
  const noLinksWidth = isBoard ? "sm" : "xs";
  const deep =
    router.asPath !== "/" &&
    router.asPath !== "/dashboard" &&
    router.asPath !== "/pwa-home";
  // console.log("router.asPath :", router.asPath);
  const registerLink = deep
    ? `/login?state=register&redirect=${encodeURIComponent(router.asPath)}`
    : `/login?state=register`;
  const loginLink = deep
    ? `/login?redirect=${encodeURIComponent(router.asPath)}`
    : `/login?`;
  // const open = useAppStore((state) => state.open);
  // const theme = useMantineTheme();
  if (router.asPath === "/pwa-home") return null;
  const isBoard24HoursOld =
    board &&
    new Date().getTime() - new Date(board?.createdAt).getTime() > 86400000;
  const isSettings = router.asPath.indexOf("/settings") > -1;
  // console.log("isSettings :", isSettings);
  const boardNeedsImage =
    board && !board?.image && isBoardAdmin && isBoard24HoursOld && !isSettings;
  // console.log("boardNeedsImage :", boardNeedsImage);
  const promptName = `board-logo`;
  const tip = board ? (
    <span>
      <p>
        Adding your club&apos;s logo helps fencers find your board more easily,
        makes your club stand out, and increases your chances of being featured!
      </p>
      <p>
        Head over to{" "}
        <Anchor component={Link} href={`${getBoardLink(board)}/settings`}>
          board settings
        </Anchor>{" "}
        to add a logo.
      </p>
    </span>
  ) : null;
  return (
    <AppShell.Header className={classes.root}>
      <Group
        style={{ height: "100%" }}
        px={20}
        justify="space-between"
        wrap="nowrap"
      >
        <Group pt={6} wrap="nowrap">
          <Transition
            transition="slide-right"
            mounted={showBack || showHome}
            duration={200}
          >
            {(transitionStyles) => (
              <>
                {showBack && (
                  <ActionIcon
                    variant="transparent"
                    style={transitionStyles}
                    onClick={() => router.back()}
                  >
                    <IconChevronLeft />
                  </ActionIcon>
                )}
                {showHome && (
                  <ActionIcon
                    variant="transparent"
                    style={transitionStyles}
                    onClick={() => router.push(token ? "/dashboard" : "/")}
                  >
                    <IconLayoutGrid />
                  </ActionIcon>
                )}
              </>
            )}
          </Transition>
          <PromptTip
            position="bottom"
            disabled={!boardNeedsImage}
            name={promptName}
            tip={tip}
          >
            <span>
              <Logo />
            </span>
          </PromptTip>
          <Group pt={2}>{board && <BoardLink board={board} />}</Group>
        </Group>
        <Transition
          mounted={userChecked}
          transition="fade"
          duration={400}
          timingFunction="ease"
        >
          {(styles) => (
            <Group style={styles} wrap="nowrap">
              {!token && !isLogin && (
                <>
                  <Hider smallerThan={noLinksWidth}>
                    <Group>
                      <Anchor size="sm" component={Link} href="/pricing">
                        {t("pricing")}
                      </Anchor>
                    </Group>
                  </Hider>
                  <Hider smallerThan={noLinksWidth}>
                    <Group>
                      <Anchor size="sm" component={Link} href="/support">
                        {t("support")}
                      </Anchor>
                    </Group>
                  </Hider>
                  <Button size="xs" component={Link} href={registerLink}>
                    {t("signUp")}
                  </Button>
                  <Hider smallerThan="xs">
                    <Box>
                      <Anchor size="sm" component={Link} href={loginLink}>
                        {t("orLogin")}
                      </Anchor>
                    </Box>
                  </Hider>
                </>
              )}
              <SchemeToggle size="xs" />
              {user && <NotificationsButton size="xs" mt={5} />}
              {user && (
                <Menu
                  width={260}
                  position="bottom-end"
                  transitionProps={{ transition: "pop-top-right" }}
                >
                  <Menu.Target>
                    <UnstyledButton className="user-menu">
                      <Group gap={7}>
                        <Avatar src={user.image} alt={user.name} size={20}>
                          {getInitials(user.name)}
                        </Avatar>
                        <IconChevronDown size={12} stroke={1.5} />
                      </Group>
                    </UnstyledButton>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Item>{user.name}</Menu.Item>

                    <Menu.Item
                      leftSection={<IconSettings size={14} stroke={1.5} />}
                      component={Link}
                      href="/settings"
                    >
                      {t("accountSettings")}
                    </Menu.Item>

                    {user.admin && (
                      <Menu.Item
                        component={Link}
                        href="/admin"
                        leftSection={<IconChevronsUp size={14} stroke={1.5} />}
                      >
                        {t("admin")}
                      </Menu.Item>
                    )}

                    <Menu.Item
                      component={Link}
                      href="/billing"
                      leftSection={
                        <IconCurrencyDollar size={14} stroke={1.5} />
                      }
                    >
                      {t("plans")}
                    </Menu.Item>
                    <Menu.Item
                      component={Link}
                      href="/payments"
                      leftSection={<IconCoin size={14} stroke={1.5} />}
                    >
                      {t("myPayments")}
                    </Menu.Item>

                    {!isPwa && (
                      <Menu.Item
                        component={Link}
                        href={isAndroid ? PLAY_STORE_LINK : "/app"}
                        target={isAndroid ? "_blank" : "_self"}
                        leftSection={
                          <IconDeviceMobile size={14} stroke={1.5} />
                        }
                      >
                        {t("installApp")}
                      </Menu.Item>
                    )}
                    <Menu.Item
                      component={Link}
                      href="/support"
                      leftSection={<IconQuestionMark size={14} stroke={1.5} />}
                    >
                      {t("support")}
                    </Menu.Item>
                    <Divider mt={5} mb={20} />
                    <Menu.Item
                      className="logout"
                      onClick={doLogout}
                      leftSection={<IconLogout size={14} stroke={1.5} />}
                    >
                      {t("logout")}
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              )}
            </Group>
          )}
        </Transition>
      </Group>
    </AppShell.Header>
  );
};
export default HeaderComp;
